import type { AxiosResponse } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { trackEvent } from '@/analytics/trackEvent';
import { TAG_API_ROUTES } from '@/api-routes';
import { axiosInstance } from '@/axios';
import { NEEDL_BOOKMARK_TAG, QUERY_KEY } from '@/components/UserTags/common';
import { useBoard } from '@/hooks/boards-hooks';
import { useCurrentChannel } from '@/hooks/query/useCurrentChannel';
import { useInvalidateNeedlbox } from '@/hooks/query/useFetchNeedlbox';
import { useQueryParams } from '@/hooks/useQueryParams';
import type { RouteParam } from '@/types';

export const useMutateDocumentTag = (deeplink: string) => {
  const queryClient = useQueryClient();
  const invalidateNeedlbox = useInvalidateNeedlbox();
  const { board_name } = useBoard();
  const { name: channel_name } = useCurrentChannel();
  const { subCategory } = useParams<RouteParam>();
  const [q] = useQueryParams();

  return useMutation(
    ({ tags, feedId, channelId }: MutationType) => {
      return axiosInstance.post<void>(TAG_API_ROUTES.deepLink(deeplink), {
        tags,
        feed_id: feedId,
        channel_id: channelId,
      });
    },
    {
      onSuccess: async (_, { tags, isBookmarked }) => {
        if (!isBookmarked && tags?.includes(NEEDL_BOOKMARK_TAG)) {
          trackEvent('bookmark_added', {
            feed_name: channel_name || board_name || q.search_type || 'home',
            search_term: q.q ?? '',
            tab_name: subCategory,
          });
        } else if (isBookmarked && !tags?.includes(NEEDL_BOOKMARK_TAG)) {
          trackEvent('bookmark_removed', {
            feed_name: channel_name || board_name || q.search_type || 'home',
            search_term: q.q ?? '',
            tab_name: subCategory,
          });
        } else {
          trackEvent('tag_added');
        }
        await Promise.all([
          queryClient.invalidateQueries([QUERY_KEY, deeplink]),
          queryClient.invalidateQueries([QUERY_KEY, 'user-tags']),
          queryClient.invalidateQueries([QUERY_KEY, 'filters']),
          invalidateNeedlbox(),
        ]);
      },
      onMutate: async ({ tags }) => {
        await queryClient.cancelQueries([QUERY_KEY, deeplink]);

        const previousDocumentTagsData = queryClient.getQueryData<DataType>([
          QUERY_KEY,
          deeplink,
        ]);

        if (!previousDocumentTagsData) {
          return;
        }

        queryClient.setQueryData<DataType>([QUERY_KEY, deeplink], {
          ...previousDocumentTagsData,
          data: {
            ...previousDocumentTagsData.data,
            results: {
              ...previousDocumentTagsData.data.results,
              user_tags: [...new Set(tags)],
            },
          },
        });

        return { previousDocumentTagsData };
      },
      onError: (
        _,
        __,
        context?: {
          previousDocumentTagsData?: DataType;
        }
      ) => {
        if (!context) {
          return;
        }

        queryClient.setQueryData(
          [QUERY_KEY, deeplink],
          context.previousDocumentTagsData
        );
      },
    }
  );
};

type MutationType = {
  tags: string[];
  feedId?: string;
  channelId?: string;
  isBookmarked: boolean;
};

type ResponseType = {
  results: {
    user_tags: string[];
    shared_tags: string[];
  };
};
export type DataType = AxiosResponse<ResponseType>;
